import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Settings2, X } from 'lucide-react';
import { updateAgenda, toMin, WEEKDAY_LABELS } from '../../lib/citasApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

/**
 * Configuración de una agenda (solo admin): nombre/color, tamaño de hueco,
 * horario semanal y duración por concepto (overrides).
 *
 * Props:
 *  - open / onOpenChange
 *  - agenda:    agenda a editar
 *  - conceptos: catálogo de conceptos (para la tabla de overrides)
 *  - onSaved:   callback con la agenda actualizada
 */

const SLOT_OPTIONS = [5, 10, 15, 20, 30];

export default function AgendaConfigDialog({ open, onOpenChange, agenda, conceptos = [], onSaved }) {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#2E6E5E');
  const [slotMinutes, setSlotMinutes] = useState('15');
  const [descripcion, setDescripcion] = useState('');
  const [wh, setWh] = useState({}); // {"0".."6": [{start, end}]}
  const [overrides, setOverrides] = useState({}); // {code: "min" | ""}
  // Solo quirófanos: límite de procedimientos por turno ('' = sin límite).
  const [maxQxManana, setMaxQxManana] = useState('');
  const [maxQxTarde, setMaxQxTarde] = useState('');
  const [alquilerModulo, setAlquilerModulo] = useState('');
  const [saving, setSaving] = useState(false);

  const esQuirofano = agenda?.tipo === 'quirofano';

  useEffect(() => {
    if (!open || !agenda) return;
    setName(agenda.name || '');
    setColor(agenda.color || '#2E6E5E');
    setSlotMinutes(String(agenda.slot_minutes || 15));
    setDescripcion(agenda.descripcion || '');
    const next = {};
    for (let i = 0; i < 7; i++) {
      next[String(i)] = (agenda.working_hours?.[String(i)] || []).map((r) => ({ ...r }));
    }
    setWh(next);
    const ov = {};
    Object.entries(agenda.concept_overrides || {}).forEach(([code, min]) => {
      ov[code] = String(min);
    });
    setOverrides(ov);
    setMaxQxManana(agenda.max_qx_manana ? String(agenda.max_qx_manana) : '');
    setMaxQxTarde(agenda.max_qx_tarde ? String(agenda.max_qx_tarde) : '');
    setAlquilerModulo(agenda.alquiler_modulo ? String(agenda.alquiler_modulo) : '');
    setSaving(false);
  }, [open, agenda]);

  const addRange = (day) => {
    setWh((prev) => {
      const ranges = prev[day] || [];
      const last = ranges[ranges.length - 1];
      const nextRange = last ? { start: last.end || '16:00', end: '20:00' } : { start: '09:00', end: '14:00' };
      return { ...prev, [day]: [...ranges, nextRange] };
    });
  };

  const removeRange = (day, idx) => {
    setWh((prev) => ({ ...prev, [day]: (prev[day] || []).filter((_, i) => i !== idx) }));
  };

  const setRange = (day, idx, field, value) => {
    setWh((prev) => ({
      ...prev,
      [day]: (prev[day] || []).map((r, i) => (i === idx ? { ...r, [field]: value } : r)),
    }));
  };

  const submit = async () => {
    if (!agenda?.id) return;
    if (!name.trim()) {
      toast.error('El nombre de la agenda es obligatorio');
      return;
    }
    // Validación del horario: todos los rangos completos y coherentes.
    const working_hours = {};
    for (let i = 0; i < 7; i++) {
      const day = String(i);
      const ranges = wh[day] || [];
      for (const r of ranges) {
        if (!r.start || !r.end || toMin(r.end) <= toMin(r.start)) {
          toast.error(`Revisa el horario del ${WEEKDAY_LABELS[i].toLowerCase()}: la hora de fin debe ser posterior a la de inicio`);
          return;
        }
      }
      working_hours[day] = ranges.map((r) => ({ start: r.start, end: r.end }));
    }
    // Overrides: solo los que difieran del default del concepto.
    const concept_overrides = {};
    for (const c of conceptos) {
      const raw = String(overrides[c.code] ?? '').trim();
      if (!raw) continue;
      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0) {
        toast.error(`Duración no válida para "${c.name}"`);
        return;
      }
      if (n !== c.default_minutes) concept_overrides[c.code] = n;
    }
    // Límites de quirófano: enteros >= 0 (vacío = sin límite = null).
    const parseLimite = (raw, etiqueta) => {
      const s = String(raw ?? '').trim();
      if (!s) return null;
      const n = Number(s);
      if (!Number.isInteger(n) || n < 0) {
        toast.error(`Límite de la ${etiqueta} no válido (número entero, 0 = sin límite)`);
        return undefined; // undefined = error de validación
      }
      return n || null; // 0 se guarda como null (sin límite)
    };
    let max_qx_manana = null;
    let max_qx_tarde = null;
    if (esQuirofano) {
      max_qx_manana = parseLimite(maxQxManana, 'mañana');
      if (max_qx_manana === undefined) return;
      max_qx_tarde = parseLimite(maxQxTarde, 'tarde');
      if (max_qx_tarde === undefined) return;
    }
    setSaving(true);
    try {
      const updated = await updateAgenda(agenda.id, {
        name: name.trim(),
        color,
        slot_minutes: Number(slotMinutes),
        descripcion: descripcion.trim(),
        working_hours,
        concept_overrides,
        ...(esQuirofano ? { max_qx_manana, max_qx_tarde } : {}),
        alquiler_modulo: Number(String(alquilerModulo).replace(',', '.')) || null,
      });
      toast.success('Agenda actualizada');
      onSaved?.(updated);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar la agenda');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="agenda-config-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-[#13231E]" />
            Configurar agenda
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Nombre + color */}
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label>Nombre</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1.5"
                data-testid="agenda-config-name"
              />
            </div>
            <div>
              <Label>Color</Label>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="mt-1.5 block h-10 w-14 rounded-md border border-input bg-white p-1 cursor-pointer"
                aria-label="Color de la agenda"
                data-testid="agenda-config-color"
              />
            </div>
          </div>

          {/* Qué se hace aquí — lo lee el Agente */}
          <div>
            <Label>Qué se hace en esta agenda</Label>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              rows={3}
              maxLength={600}
              placeholder="Ej.: procedimiento A, procedimiento B"
              className="mt-1.5 w-full rounded-md border border-input bg-white p-2 text-sm"
              data-testid="agenda-config-descripcion"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Los procedimientos que se atienden aquí, separados por comas. <strong>El Agente
              lo usa para saber a qué profesional mandar cada paciente</strong>: si lo dejas
              vacío, tendrá que preguntar en vez de proponer.
            </p>
          </div>

          {/* Tamaño de hueco */}
          <div>
            <Label>Tamaño de hueco</Label>
            <Select value={slotMinutes} onValueChange={setSlotMinutes}>
              <SelectTrigger className="mt-1.5 w-40" data-testid="agenda-config-slot">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SLOT_OPTIONS.map((m) => (
                  <SelectItem key={m} value={String(m)}>
                    {m} min
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">Las citas empiezan siempre en múltiplos de este valor.</p>
          </div>

          {/* Solo quirófanos: límite de procedimientos por turno */}
          {esQuirofano && (
            <div data-testid="agenda-config-limites-qx">
              <Label>Límite de procedimientos por turno</Label>
              <div className="grid grid-cols-2 gap-3 mt-1.5">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Mañana (hasta 15:00)</div>
                  <Input
                    type="number"
                    min="0"
                    value={maxQxManana}
                    onChange={(e) => setMaxQxManana(e.target.value)}
                    placeholder="Sin límite"
                    data-testid="agenda-config-max-manana"
                  />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Tarde (desde 15:00)</div>
                  <Input
                    type="number"
                    min="0"
                    value={maxQxTarde}
                    onChange={(e) => setMaxQxTarde(e.target.value)}
                    placeholder="Sin límite"
                    data-testid="agenda-config-max-tarde"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Al llegar al límite, ese turno deja de ofrecer huecos (el admin puede forzar).
                Vacío o 0 = sin límite.
              </p>
            </div>
          )}

          {/* Alquiler de módulo a un externo (2026-07-28). Solo deja
              constancia de los módulos usados: no cobra ni toca la caja. */}
          <div data-testid="agenda-config-alquiler">
            <Label>Alquiler de módulo (externos)</Label>
            <Input
              type="text"
              inputMode="decimal"
              className="mt-1.5"
              value={alquilerModulo}
              onChange={(e) => setAlquilerModulo(e.target.value)}
              placeholder="Vacío = no alquila"
              data-testid="agenda-config-alquiler-modulo"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Lo que se le cobra por CADA módulo usado. Mañana y tarde cuentan
              por separado (corte a las 15:00). Los módulos usados y su total
              salen en Honorarios; aquí no se cobra nada.
            </p>
          </div>

          <Separator />

          {/* Horario semanal */}
          <div>
            <Label>Horario semanal</Label>
            <div className="mt-2 space-y-1.5">
              {WEEKDAY_LABELS.map((label, i) => {
                const day = String(i);
                const ranges = wh[day] || [];
                return (
                  <div key={day} className="flex items-start gap-3 py-1" data-testid={`agenda-config-day-${day}`}>
                    <div className="w-24 shrink-0 pt-1.5 text-sm font-medium text-[#13231E]">{label}</div>
                    <div className="flex-1 min-w-0 space-y-1.5">
                      {ranges.length === 0 && (
                        <div className="text-sm text-muted-foreground/70 pt-1.5">Cerrado</div>
                      )}
                      {ranges.map((r, idx) => (
                        <div key={idx} className="flex items-center gap-1.5">
                          <Input
                            type="time"
                            value={r.start || ''}
                            onChange={(e) => setRange(day, idx, 'start', e.target.value)}
                            className="w-28 tabular-nums"
                            data-testid={`agenda-config-day-${day}-start-${idx}`}
                          />
                          <span className="text-muted-foreground text-sm">–</span>
                          <Input
                            type="time"
                            value={r.end || ''}
                            onChange={(e) => setRange(day, idx, 'end', e.target.value)}
                            className="w-28 tabular-nums"
                            data-testid={`agenda-config-day-${day}-end-${idx}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-red-700"
                            onClick={() => removeRange(day, idx)}
                            aria-label={`Quitar rango del ${label.toLowerCase()}`}
                            data-testid={`agenda-config-day-${day}-remove-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 mt-0.5 text-[#2E6E5E] hover:text-[#13231E]"
                      onClick={() => addRange(day)}
                      aria-label={`Añadir rango al ${label.toLowerCase()}`}
                      data-testid={`agenda-config-day-${day}-add`}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>

          <Separator />

          {/* Overrides de duración por concepto */}
          <div>
            <Label>Duración por concepto (min)</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">
              Deja el campo vacío para usar la duración por defecto del concepto.
            </p>
            <div className="rounded-md border border-[#D9E3DD] divide-y divide-[#F0F6F2]">
              {conceptos.map((c) => (
                <div key={c.code} className="flex items-center gap-3 px-3 py-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: c.color || '#2E6E5E' }}
                    aria-hidden="true"
                  />
                  <div className="flex-1 min-w-0 text-sm text-[#13231E] truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground shrink-0">por defecto {c.default_minutes} min</div>
                  <Input
                    type="number"
                    min={5}
                    step={5}
                    value={overrides[c.code] ?? ''}
                    onChange={(e) => setOverrides((prev) => ({ ...prev, [c.code]: e.target.value }))}
                    placeholder={String(c.default_minutes)}
                    className="w-20 shrink-0 tabular-nums"
                    data-testid={`agenda-config-override-${c.code}`}
                  />
                </div>
              ))}
              {conceptos.length === 0 && (
                <div className="px-3 py-4 text-sm text-muted-foreground">No hay conceptos de cita definidos.</div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="agenda-config-cancel">
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={saving}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            data-testid="agenda-config-save"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
